import PropTypes from "prop-types";
import React from "react";
import {
  Grid,
  MenuItem,
  FormControl,
  OutlinedInput,
  CircularProgress,
  Select,
  Card,
  Backdrop,
} from "@mui/material";
import SoftInput from "components/SoftInput";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useForm } from "react-hook-form";
import SoftButton from "components/SoftButton";
import useAPI from "hooks/useAPI";
import { Controller } from "react-hook-form";
import slugify from "slugify";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Link, useNavigate, useParams } from "react-router-dom";
import Loader from "components/loader";
import useAlert from "hooks/useAlert";
import AlertMessage from "components/UI/AlertMessage";
import AttributeField from "./AttributeField";
import { flattenErrors } from "utils/utilities";
import { useTranslation } from "react-i18next";

const ControllerForm = () => {
  const { t } = useTranslation();
  const { api } = useAPI();
  const [controllerTypes, setControllerTypes] = React.useState([]);
  const [manufacturers, setManufacturers] = React.useState([]);
  const [btnLoading, setBtnLoading] = React.useState(false);
  const [formLoading, setFormLoading] = React.useState(false);
  const [originalSlug, setOriginalSlug] = React.useState("");
  const [originalName, setOriginalName] = React.useState("");
  const [nameTouched, setNameTouched] = React.useState(false);
  const [attributes, setAttributes] = React.useState([]);
  const navigate = useNavigate();
  const { errorState, showAlert } = useAlert();
  const { editControllerId } = useParams();
  const [selectedValues, setSelectedValues] = React.useState({});

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
    clearErrors,
    reset,
    setFocus,
    setValue,
  } = useForm();

  React.useEffect(() => {
    const fetchControllerTypes = async () => {
      // if (!open) return; // Only fetch if the dialog is open
      setFormLoading(true);
      try {
        const response = await api.get("/controller/types");
        if (response.status === 200) {
          setControllerTypes(response.data.controller_types || []);
        } else {
          console.error("Failed to fetch controller types:", response.data.error);
        }
      } catch (error) {
        console.error("Error fetching controller types:", error);
      } finally {
        setFormLoading(false);
      }
    };

    const fetchManufacturers = async () => {
      try {
        const response = await api.get("/manufacturers/");
        if (response.status === 200) {
          setManufacturers(response.data?.data || []);
        } else {
          console.error("Failed to fetch manufacturers:", response.data.error);
        }
      } catch (error) {
        console.error("Error fetching manufacturers:", error);
      }
    };

    fetchControllerTypes();
    fetchManufacturers();
    //
    if (editControllerId) {
      // If editing, fetch the controller data
      api
        .get(`/controller/${editControllerId}/`)
        .then((response) => {
          if (response.status === 200) {
            const controllerData = response.data;
            setOriginalSlug(controllerData.slug);
            setOriginalName(controllerData.name);
            reset({
              name: controllerData.name,
              slug: controllerData.slug,
              description: controllerData.description,
              code: controllerData.code,
              // manufacturer_code: controllerData.manufacturer_code,
              manufacturer_id: controllerData.manufacturer_id,
              controller_type: controllerData.controller_type,
              attributes: controllerData?.attributes || {},
            });
            setSelectedValues(controllerData?.attributes || {});
          } else {
            showAlert("error", response.data.error);
          }
        })
        .catch((error) => {
          console.error("Error fetching controller data:", error);
          showAlert("error", "Failed to fetch controller data.");
        });
    }
  }, [editControllerId]);

  const onFormSubmit = async (formData) => {
    setBtnLoading(true);

    try {
      let response;
      if (editControllerId) {
        // If editing, send a PUT request
        response = await api.put(`/controller/${editControllerId}/`, formData);
        // setEditControllerId(null);
        setNameTouched(false);
      } else {
        // If creating, send a POST request}
        response = await api.post("/controllers/", formData);
      }

      if (response.status === 201 || response.status === 200) {
        sessionStorage.setItem("alert", response.data.message);
        navigate("/controllers");
      } else {
        showAlert("error", response.data.error);
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.errors) {
        const backendErrors = error?.response?.data?.errors || {};
        // console.log("backendErrors", backendErrors);
        if (backendErrors) {
          const flattened = flattenErrors(backendErrors);
          for (const field in flattened) {
            setError(field, { type: "manual", message: t(flattened[field]) });
            setFocus(field);
          }
        }
      } else {
        showAlert("error", error?.response?.data?.error || "An unexpected error occurred");
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const nameValue = watch("name");

  React.useEffect(() => {
    if (editControllerId && nameValue !== undefined && nameValue != "") {
      if (!nameTouched && nameValue !== originalName) {
        setNameTouched(true);
      }
    }
    if (!editControllerId) {
      setNameTouched(true);
    }
  }, [nameValue, nameTouched, editControllerId]);

  const generatedSlug = slugify(nameValue || "", { lower: true, strict: true }) + "-controller";
  const slugToShow = nameTouched ? generatedSlug : originalSlug;

  React.useEffect(() => {
    setValue("slug", slugToShow);
  }, [slugToShow, setValue]);

  const CONTROLLER_TYPE = watch("controller_type", null);

  const getAttributes = async (controller_type) => {
    const response = await api.get(`/controller-type/${controller_type}/attributes/`);
    if (response.status == 200) {
      setAttributes(response.data);
    }
  };

  React.useEffect(() => {
    if (CONTROLLER_TYPE) {
      getAttributes(CONTROLLER_TYPE);
    } else {
      setAttributes([]);
    }
  }, [CONTROLLER_TYPE]);

  const pagesList = [
    {
      name: t("lang.controllers"),
      route: "/controllers",
    },
    {
      name: editControllerId ? t("lang.edit_controller") : t("lang.add_controller"),
      route: "",
    },
  ];
  return (
    <>
      <DashboardLayout>
        <DashboardNavbar pagesList={pagesList} />
        <Card
          sx={{
            p: 3,
          }}
        >
          <SoftBox>
            <SoftTypography>{t("lang.controller_form")}</SoftTypography>
          </SoftBox>
          <SoftBox
            component="form"
            role="form"
            sx={{ position: "relative" }}
            onSubmit={handleSubmit(onFormSubmit)}
          >
            <Backdrop
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 253, 253, 0.71)",
                zIndex: 1,
              }}
              open={formLoading}
            >
              <Loader color="inherit" />
            </Backdrop>

            <Grid container spacing={1} mb={3}>
              <Grid item xs={12} md={6}>
                <SoftBox>
                  <SoftTypography variant="overline">
                    {t("lang.name")}

                    {watch("name") && (
                      <SoftTypography variant="caption" color="text">
                        {" "}
                        ({t("lang.slug")}:{" "}
                        <span style={{ fontStyle: "italic", color: "#666" }}>{slugToShow}</span>
                        <input {...register("slug")} type="hidden" name="slug" value={slugToShow} />
                        )
                      </SoftTypography>
                    )}
                  </SoftTypography>
                  <SoftInput
                    {...register("name", {
                      required: t("lang.validations.required", {
                        field: t("lang.name"),
                      }),
                    })}
                    fullWidth
                  />
                  {errors.name && (
                    <SoftTypography variant="caption" color="error">
                      {errors.name.message}
                      <br />
                    </SoftTypography>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftBox>
                  <FormControl fullWidth>
                    <SoftTypography variant="overline" gutterBottom>
                      {t("lang.type")}
                    </SoftTypography>
                    <Controller
                      name="controller_type"
                      control={control}
                      rules={{
                        required: t("lang.validations.required", {
                          field: t("lang.type"),
                        }),
                      }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            field.onChange(e.target.value); // update form state
                            clearErrors("controller_type"); // clear any errors
                          }}
                          fullWidth
                          input={<OutlinedInput sx={{ height: 56 }} />}
                          sx={{ padding: "10px" }}
                          error={!!errors.controller_type}
                        >
                          <MenuItem key="" value="">
                            {t("lang.choose")}
                          </MenuItem>
                          {controllerTypes?.map(({ id, label }) => (
                            <MenuItem key={id} value={id}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>

                  {errors.controller_type && (
                    <SoftTypography variant="caption" color="error">
                      {errors.controller_type.message}
                    </SoftTypography>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftBox>
                  <SoftTypography variant="overline">{t("lang.code")}</SoftTypography>
                  <SoftInput
                    {...register("code", {
                      required: t("lang.validations.required", {
                        field: t("lang.code"),
                      }),
                    })}
                    fullWidth
                  />
                  {errors.code && (
                    <SoftTypography variant="caption" color="error">
                      {errors.code.message}
                    </SoftTypography>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftBox>
                  {/* <SoftTypography variant="overline">{t("lang.manufacturer_code")}</SoftTypography>
                  <SoftInput
                    {...register("manufacturer_code", {
                      required: t("lang.validations.required", {
                        field: t("lang.manufacturer_code"),
                      }),
                    })}
                    fullWidth
                  /> */}
                  <FormControl fullWidth>
                    <SoftTypography variant="overline" gutterBottom>
                      {t("lang.manufacturer")}
                    </SoftTypography>
                    <Controller
                      name="manufacturer_id"
                      control={control}
                      rules={{
                        required: t("lang.validations.required", {
                          field: t("lang.manufacturer"),
                        }),
                      }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            field.onChange(e.target.value); // update form state
                            clearErrors("manufacturer_id"); // clear any errors
                          }}
                          fullWidth
                          input={<OutlinedInput sx={{ height: 56 }} />}
                          sx={{ padding: "10px" }}
                          error={!!errors.manufacturer_id}
                        >
                          <MenuItem key="" value="">
                            {t("lang.choose")}
                          </MenuItem>
                          {manufacturers?.map(({ id, name }) => (
                            <MenuItem key={id} value={id}>
                              {name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                  {errors.manufacturer_id && (
                    <SoftTypography variant="caption" color="error">
                      {errors.manufacturer_id.message}
                    </SoftTypography>
                  )}
                </SoftBox>
              </Grid>
              <Grid item xs={12} md={12} mb={2}>
                <SoftBox>
                  <SoftTypography variant="overline">{t("lang.description")}</SoftTypography>
                  <SoftInput {...register("description")} fullWidth />
                  {errors.description && (
                    <SoftTypography variant="caption" color="error">
                      {errors.description.message}
                    </SoftTypography>
                  )}
                </SoftBox>
              </Grid>
              {attributes.length > 0 &&
                attributes.map((field, index) => {
                  return (
                    <AttributeField
                      key={index}
                      fieldData={field.field_data}
                      label={field.label}
                      control={control}
                      register={register}
                      errors={errors}
                      setSelectedValues={setSelectedValues}
                      selectedValues={selectedValues}
                    />
                  );
                })}
            </Grid>
            <SoftButton
              type="submit"
              variant="contained"
              color="dark"
              disabled={btnLoading}
              startIcon={btnLoading ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {editControllerId ? t("lang.actions.update") : t("lang.actions.save")}
            </SoftButton>
            <Link to="/controllers">
              <SoftButton variant="text" color="dark">
                {t("lang.actions.back")}
              </SoftButton>
            </Link>
          </SoftBox>
        </Card>
        <AlertMessage {...errorState} />
      </DashboardLayout>
    </>
  );
};

export default ControllerForm;
